import { ColumnsType } from "antd/es/table";
import {
  RatingTag,
  ReportRating,
} from "../../../agent/AgentContent/AIXRay/Rating";
import { BaseTable } from "../../TokenSpotlights/Crypto/components";
import { Stock, StockMarketEnum, Token } from "@/src/features/api/openData";
import {
  StocksQuestParams,
  useIsPc,
  useStocksRankingInfiniteScroll,
} from "@/src/hooks";

import { I18nUtil } from "@utils/i18n";
import { StockMarketCap } from "@/src/components/table/stock/columns/StockMarketCap";
import { StockPriceChange } from "@/src/components/table/stock/columns/StockPriceChange";
import { StockResult } from "@/src/hooks/useStockDashboard";
import { StockToken } from "@/src/components/table/stock/columns/StockToken";
import Translations from "@translate/components/Home/Report/Stock/StockReport";
import { useGoToDetail } from "@/src/hooks/useGoToDetail";
import { StockType } from "@/src/utils/stockType";

const I18n = I18nUtil.createScoped(Translations);

type ExtendedToken = Token & {
  latest_rating?: {
    level?: {
      tokenomics?: string;
      final?: string;
      fundamental?: string;
      momentum?: string;
    };
    version?: string;
  };
};

export const StockReport = ({
  activeTab: _activeTab,
  market,
}: {
  activeTab: string;
  market: StockType;
}) => {
  const [isPC] = useIsPc();
  const { data, isLoading } = useStocksRankingInfiniteScroll({
    queryParams: {
      type: "REPORT_360",
      limit: 5,
      [StocksQuestParams.Enrich_With_Rating]: true,
      [StocksQuestParams.EnRich_Enable]: true,
      [StocksQuestParams.Enrich_With_Trading_Data]: true,
      market: market as StockMarketEnum,
    },
  });

  const columns: ColumnsType<ExtendedToken> = [
    {
      title: I18n.t("Stock"),
      dataIndex: "stock",
      key: "stock",
      fixed: "left",
      align: "left",
      width: 160,
      render: (_value: string, record: Stock) => (
        <StockToken
          logo={record?.logo ?? ""}
          symbol={record?.symbol ?? ""}
          companyName={record?.company_name ?? ""}
          tag={record?.tags?.[0] ?? ""}
          className="max-w-[100px]"
        />
      ),
    },
    {
      title: I18n.t("Change"),
      dataIndex: "change",
      key: "change",
      width: 100,
      align: "left",
      render: (_: unknown, record: StockResult) => {
        return <StockPriceChange stock={record} />;
      },
    },
    {
      title: I18n.t("Market Cap"),
      dataIndex: "market_data",
      key: "marketCap",
      width: 90,
      align: "left",
      render: (_value: string, record: Stock) => {
        return <StockMarketCap stock={record} />;
      },
    },
    {
      title: I18n.t("Valuation"),
      dataIndex: "latest_rating",
      key: "valuation",
      width: 80,
      align: "left",
      render: (rating: Stock["latest_rating"]) => (
        <RatingTag rating={rating?.level?.valuation ?? ""} />
      ),
    },
    {
      title: I18n.t("Growth"),
      dataIndex: "latest_rating",
      key: "growth",
      width: 80,
      align: "left",
      render: (rating: Stock["latest_rating"]) => (
        <RatingTag rating={rating?.level?.growth ?? ""} />
      ),
    },
    {
      title: I18n.t("Profitability"),
      dataIndex: "latest_rating",
      key: "profitability",
      width: 80,
      align: "left",
      render: (rating: Stock["latest_rating"]) => (
        <RatingTag rating={rating?.level?.momentum ?? ""} />
      ),
    },
    {
      title: I18n.t("Momentum"),
      dataIndex: "latest_rating",
      key: "momentum",
      align: "left",
      width: 80,
      render: (rating: Stock["latest_rating"]) => (
        <RatingTag rating={rating?.level?.momentum ?? ""} />
      ),
    },
    {
      title: I18n.t("Rating"),
      dataIndex: "latest_rating",
      key: "rating",
      align: "center",
      fixed: "right",
      width: isPC ? 100 : 60,
      render: (rating: Stock["latest_rating"], record: Stock) => (
        <ReportRating
          rating={rating?.level?.final ?? ""}
          symbol={record?.symbol ?? ""}
          id={record?.id ?? ""}
          type="stock"
        />
      ),
    },
  ];
  const { onDetail, isLoading: isLoadingDetail } = useGoToDetail("stock");
  const onRow = (record: Stock) => {
    onDetail(record);
  };
  return (
    <BaseTable
      columns={columns}
      dataSource={data ?? []}
      loading={isLoading}
      onRow={onRow}
      isSearchLoading={isLoadingDetail}
    />
  );
};
